import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adHocFeedbackKeys } from "@/lib/query";
import type { GiveAdHocFeedbackInput } from "@/schemas/review-adhoc-feedback";

/** Standalone feedback given outside any review cycle — EPIC-067. */

export interface AdHocFeedbackItem {
  id: string;
  body: string;
  sentimentKey: string;
  sentimentName: string;
  isAnonymous: boolean;
  isVisibleToEmployee: boolean;
  authorName: string | null;
  /** Set only for post-project feedback — EPIC-068 WI-250. */
  relatedProjectName: string | null;
  createdAt: string;
}

export interface AdHocFeedbackForPerson {
  role: "self" | "manager" | "none";
  items: AdHocFeedbackItem[];
}

export interface FeedbackProjectSearchResult {
  projectId: string;
  projectName: string;
}

async function failure(res: Response, fallback: string): Promise<Error> {
  const body = await res.json().catch(() => null);
  const message =
    body && typeof body === "object" && "error" in body && typeof body.error === "string"
      ? body.error
      : fallback;
  return new Error(message);
}

/** A person's ad hoc feedback, scoped by the server to what the viewer may see. */
export function useAdHocFeedbackForPerson(personId: string, enabled = true) {
  return useQuery({
    queryKey: adHocFeedbackKeys.forPerson(personId),
    enabled: enabled && Boolean(personId),
    queryFn: async (): Promise<AdHocFeedbackForPerson> => {
      const res = await fetch(`/api/feedback/ad-hoc/person/${personId}`);
      if (!res.ok) throw await failure(res, "Couldn't load feedback");
      return (await res.json()).data;
    },
  });
}

export function useGiveAdHocFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: GiveAdHocFeedbackInput) => {
      const res = await fetch("/api/feedback/ad-hoc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await failure(res, "Couldn't submit that feedback");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: adHocFeedbackKeys.forPerson(variables.subjectPersonId),
      });
      toast.success("Feedback submitted");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** NOS project search for the post-project feedback picker — EPIC-068 WI-250. */
export function useSearchProjectsForFeedback(search: string) {
  return useQuery({
    queryKey: adHocFeedbackKeys.projectSearch(search),
    queryFn: async (): Promise<FeedbackProjectSearchResult[]> => {
      const res = await fetch(`/api/feedback/projects/search?search=${encodeURIComponent(search)}`);
      if (!res.ok) throw await failure(res, "Couldn't reach NOS to search projects");
      return (await res.json()).data;
    },
    enabled: search.trim().length > 0,
  });
}

/** The manager's release decision for one ad hoc note. */
export function useSetAdHocFeedbackVisibility(subjectPersonId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      feedbackId,
      isVisibleToEmployee,
    }: {
      feedbackId: string;
      isVisibleToEmployee: boolean;
    }) => {
      const res = await fetch(`/api/feedback/ad-hoc/${feedbackId}/visibility`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isVisibleToEmployee }),
      });
      if (!res.ok) throw await failure(res, "Couldn't change who sees this");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adHocFeedbackKeys.forPerson(subjectPersonId) });
      toast.success(
        variables.isVisibleToEmployee ? "Shared with them" : "No longer shared with them",
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** A finished project the viewer is invited to give feedback on — EPIC-068 WI-249. */
export interface ProjectFeedbackPrompt {
  id: string;
  title: string;
  endedOn: string | null;
  nosProjectId: string | null;
  projectName: string | null;
  colleagues: { personId: string; name: string }[];
}

/**
 * The viewer's end-of-project prompts. An error (the feature being off answers
 * 404) is treated as "nothing to show" by the card, so it is not retried.
 */
export function useMyProjectPrompts() {
  return useQuery({
    queryKey: adHocFeedbackKeys.projectPrompts,
    queryFn: async (): Promise<ProjectFeedbackPrompt[]> => {
      const res = await fetch("/api/me/project-feedback-prompts");
      if (!res.ok) throw await failure(res, "Failed to load your project feedback prompts");
      return (await res.json()).data;
    },
    retry: false,
  });
}

export function useDismissProjectPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/me/project-feedback-prompts/${id}/dismiss`, { method: "POST" });
      if (!res.ok) throw await failure(res, "Couldn't dismiss that");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adHocFeedbackKeys.projectPrompts }),
    onError: (error: Error) => toast.error(error.message),
  });
}
